import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GET_SURVEY_QUESTIONS_BY_SURVEY_ID } from "graphql/surveyQuestion/survey_question.query";
import { GET_ANSWERS_BY_QUESTION_ID } from "graphql/answer/answer.query";
import { GET_SURVEY_BY_ID } from "graphql/survey/survey.query";
import { Answer } from "types/answer";
import { TABLE_ACTIONS } from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";
import { NotificationSeverity, Messages } from "helpers/constants";

type Submission = {
  submissionId: string;
  user?: {
    user_id?: string;
    name?: string;
    email?: string;
    picture_url?: string;
  };
  created_at?: string;
  answers: Answer[];
  answerCount: number;
};

export const useAnswersList = () => {
  const { surveyId, organizationId } = useParams();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [surveyName, setSurveyName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [deleteSubmissionOpen, setDeleteSubmissionOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState<string>("");
  const { notify } = useNotifications();

  const [getSurveyQuestions] = useLazyQuery(GET_SURVEY_QUESTIONS_BY_SURVEY_ID);
  const [getAnswersByQuestionId] = useLazyQuery(GET_ANSWERS_BY_QUESTION_ID);
  const [getSurveyById] = useLazyQuery(GET_SURVEY_BY_ID);

  const fetchSubmissions = useCallback(async () => {
    if (!surveyId) return;

    setLoading(true);
    setError(null);

    try {
      // First, get survey details to get the name
      const { data: surveyData } = await getSurveyById({
        variables: { surveyId },
      });

      if (surveyData?.survey_by_pk?.name) {
        setSurveyName(surveyData.survey_by_pk.name);
      }

      // Then, get all questions for this survey
      const { data: surveyQuestionsData } = await getSurveyQuestions({
        variables: { surveyId },
      });

      if (
        !surveyQuestionsData?.survey_question ||
        surveyQuestionsData.survey_question.length === 0
      ) {
        setSubmissions([]);
        setLoading(false);
        return;
      }

      // Get all question IDs
      const questionIds = surveyQuestionsData.survey_question.map(
        (sq: any) => sq.question_id,
      );

      // Fetch answers for all questions
      const allAnswers: Answer[] = [];
      for (const questionId of questionIds) {
        const { data: answersData } = await getAnswersByQuestionId({
          variables: { questionId },
        });

        if (answersData?.answer) {
          const questionData = surveyQuestionsData.survey_question.find(
            (sq: any) => sq.question_id === questionId,
          );

          const answersForQuestion: Answer[] = (
            answersData.answer as any[]
          ).map((ans: any) => ({
            answer_id: ans.answer_id,
            answer: ans.answer,
            option_id: ans.option_id || undefined,
            question_id: ans.question_id,
            user_id: ans.user_id || undefined,
            created_at: ans.created_at || undefined,
            option: ans.option
              ? {
                  option_id: ans.option.option_id,
                  order: ans.option.order,
                  question_id: ans.option.question_id,
                  text: ans.option.text,
                }
              : undefined,
            question: questionData?.question
              ? {
                  question_id: questionData.question.question_id,
                  title: questionData.question.title,
                  type: questionData.question.type,
                  body: questionData.question.body,
                  order: questionData.question.order,
                  options: questionData.question.options?.map((opt: any) => ({
                    option_id: opt.option_id,
                    text: opt.text || "",
                    order: opt.order || 0,
                    question_id: questionData.question.question_id || "",
                  })) || [],
                }
              : undefined,
            user: ans.user
              ? {
                  user_id: ans.user.user_id,
                  name: ans.user.name || "",
                  email: ans.user.email || "",
                  picture_url: ans.user.picture_url || undefined,
                }
              : undefined,
          }));

          allAnswers.push(...answersForQuestion);
        }
      }

      // Group answers by user_id and created_at (submission)
      const submissionsMap = new Map<string, Submission>();
      
      for (const answer of allAnswers) {
        // Use milliseconds instead of ISO date format
        const timestamp = answer.created_at 
          ? new Date(answer.created_at).getTime().toString()
          : "0";
        const submissionKey = `${answer.user_id || "anonymous"}_${timestamp}`;
        
        if (!submissionsMap.has(submissionKey)) {
          submissionsMap.set(submissionKey, {
            submissionId: submissionKey,
            user: answer.user,
            created_at: answer.created_at,
            answers: [],
            answerCount: 0,
          });
        }
        
        const submission = submissionsMap.get(submissionKey)!;
        submission.answers.push(answer);
        submission.answerCount = submission.answers.length;
      }

      // Convert to array and sort by date (most recent first)
      const submissionsArray = Array.from(submissionsMap.values()).sort((a, b) => {
        if (!a.created_at || !b.created_at) return 0;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      setSubmissions(submissionsArray);
    } catch (err: any) {
      setError(err);
      console.error("Error fetching submissions:", err);
    } finally {
      setLoading(false);
    }
  }, [surveyId, getSurveyQuestions, getAnswersByQuestionId, getSurveyById]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.VIEW:
        navigate(
          `${organizationId ? `/organization/${organizationId}` : ""}/survey/${surveyId}/answers/${encodeURIComponent(id)}`,
        );
        break;
      case TABLE_ACTIONS.DELETE:
        setSubmissionToDelete(id);
        setDeleteSubmissionOpen(true);
        break;
      default:
        break;
    }
  };

  const handleDelete = async () => {
    // TODO: Implement delete functionality
    // This would need to delete all answers for a specific submission
    notify({
      message: "Delete functionality not yet implemented",
      severity: NotificationSeverity.info,
    });
    setDeleteSubmissionOpen(false);
  };

  return {
    submissions,
    surveyName,
    loading,
    error,
    organizationId,
    surveyId,
    handleActionClick,
    handleDelete,
    deleteSubmissionOpen,
    setDeleteSubmissionOpen,
    submissionToDelete,
    refetch: fetchSubmissions,
  };
};
